import Cookies from "js-cookie";

const TOKEN_KEY = "authToken";
const PENDING_ENROLL_KEY = "pendingEnrollCourseId";
const MEMBER_NO_KEY = "memberNo";
const THEME_COLOR_KEY = "themeColor";
const COURSE_PROGRESS_PREFIX = "course-progress-";
const TRACKED_SHARES_KEY = "trackedShares";
const MAX_TRACKED_SHARES = 50;
const isProd = process.env.NODE_ENV === "production";

// ── Auth token ──
export const setAuthToken = (token: string) => {
  Cookies.set(TOKEN_KEY, token, {
    expires: 30, // 30 days
    path: "/",
    secure: isProd,
    sameSite: "lax",
  });
};

export const getAuthToken = () => Cookies.get(TOKEN_KEY);

export const removeAuthToken = () => Cookies.remove(TOKEN_KEY, { path: "/" });

export const isAuthenticated = () => !!getAuthToken();

// ── Pending course enrollment (survives full-page redirects) ──
export const setPendingEnrollCourseId = (id: number | string) => {
  Cookies.set(PENDING_ENROLL_KEY, String(id), {
    expires: 1, // days
    path: "/",
    secure: isProd,
    sameSite: "lax",
  });
};

// Returns a valid positive number or null (covers "null"/"undefined"/"NaN" strings)
export const getPendingEnrollCourseId = (): number | null => {
  const raw = Cookies.get(PENDING_ENROLL_KEY);
  const id = Number(raw);
  return raw && Number.isFinite(id) && id > 0 ? id : null;
};

export const clearPendingEnrollCourseId = () =>
  Cookies.remove(PENDING_ENROLL_KEY, { path: "/" });

// ── Facilitator member number (used when enrolling in a course) ──
export const setMemberNo = (memberNo: string) => {
  Cookies.set(MEMBER_NO_KEY, memberNo, {
    expires: 30,
    path: "/",
    secure: isProd,
    sameSite: "lax",
  });
};

export const getMemberNo = (): string | null => {
  const raw = Cookies.get(MEMBER_NO_KEY);
  return raw && raw !== "null" && raw !== "undefined" ? raw : null;
};

export const clearMemberNo = () => Cookies.remove(MEMBER_NO_KEY, { path: "/" });

// ── School theme color (name like "blue" or a hex code like "#06b6d4") ──
export const setThemeColor = (theme: string) => {
  Cookies.set(THEME_COLOR_KEY, theme, {
    expires: 30,
    path: "/",
    secure: isProd,
    sameSite: "lax",
  });
};

export const getThemeColor = (): string | null => {
  const raw = Cookies.get(THEME_COLOR_KEY);
  return raw && raw !== "null" && raw !== "undefined" ? raw : null;
};

export const clearThemeColor = () =>
  Cookies.remove(THEME_COLOR_KEY, { path: "/" });

// ── Per-course reading progress (module + material position) ──
export interface CourseProgress {
  moduleIndex: number;
  materialIndex: number;
}

export const setCourseProgress = (
  slug: string | number,
  progress: CourseProgress,
) => {
  Cookies.set(
    `${COURSE_PROGRESS_PREFIX}${slug}`,
    JSON.stringify({ ...progress, timestamp: new Date().toISOString() }),
    {
      expires: 365,
      path: "/",
      secure: isProd,
      sameSite: "lax",
    },
  );
};

// Returns null when missing or malformed
export const getCourseProgress = (
  slug: string | number,
): CourseProgress | null => {
  const raw = Cookies.get(`${COURSE_PROGRESS_PREFIX}${slug}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      Number.isInteger(parsed?.moduleIndex) &&
      Number.isInteger(parsed?.materialIndex)
    ) {
      return {
        moduleIndex: parsed.moduleIndex,
        materialIndex: parsed.materialIndex,
      };
    }
    return null;
  } catch {
    return null;
  }
};

export const clearCourseProgress = (slug: string | number) =>
  Cookies.remove(`${COURSE_PROGRESS_PREFIX}${slug}`, { path: "/" });

const hashShareCode = (code: string): string => {
  let h = 5381;
  for (let i = 0; i < code.length; i++) {
    h = ((h << 5) + h + code.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
};

const readTrackedShares = (): string[] => {
  try {
    const raw = Cookies.get(TRACKED_SHARES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const hasTrackedShare = (code: string): boolean =>
  readTrackedShares().includes(hashShareCode(code));

export const markShareTracked = (code: string) => {
  const hash = hashShareCode(code);
  const list = readTrackedShares().filter((h) => h !== hash);
  list.push(hash);
  Cookies.set(
    TRACKED_SHARES_KEY,
    JSON.stringify(list.slice(-MAX_TRACKED_SHARES)),
    {
      expires: 30,
      path: "/",
      secure: isProd,
      sameSite: "lax",
    },
  );
};
